import styled, { css, keyframes } from 'styled-components'

export const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin: 2rem 0;
  justify-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

export const PricingCard = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  border: 0.5px solid ${({ theme }) => theme.palette.border.main};
  transition: all 0.3s ease;
  background-color: ${({ theme }) => theme.palette.background.default};
  position: relative;
  overflow: hidden;
  padding: 1rem;
  width: 100%;
  max-width: 400px;
`

export const PricingHeader = styled.div`
  margin-bottom: 1.5rem;
  text-align: center;
`

export const PricingTitle = styled.h3`
  margin: 0 0 1rem;
  font-size: 1.5rem;
  font-weight: 600;
`

export const PricingPrice = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.palette.text.primary};
  display: flex;
  align-items: flex-end;
  justify-content: center;
`

export const PricingPeriod = styled.span`
  font-size: 1rem;
  font-weight: 400;
  margin-left: 0.25rem;
`

export const TaxNote = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.palette.text.secondary};
  margin-top: 0.5rem;
  text-align: center;
  opacity: 0.85;
  letter-spacing: 0.02em;
`

export const FeatureList = styled.ul`
  list-style-type: none;
  margin: 0 0 2rem;
  padding: 0;
  flex-grow: 1;

  li {
    padding: 0.75rem 0;
    position: relative;
    padding-left: 1.25rem;
    padding-right: 0;

    &:before {
      content: '✓';
      position: absolute;
      left: 0;
      color: ${({ theme }) => theme.palette.success.main};
    }
  }
`

export const PricingFooter = styled.div`
  margin-top: auto;
  padding: 1rem;
`

// Pricing toggle components
export const PricingToggleContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 2rem 0;
`

export const PricingToggle = styled.div`
  display: flex;
  border-radius: 50px;
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  border: 1px solid ${({ theme }) => theme.palette.border.main};
`

export const PricingToggleButton = styled.button<{ active: boolean }>`
  padding: 0.75rem 1.5rem;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  border: none;
  outline: none;
  transition: all 0.3s ease;
  position: relative;

  ${({ active, theme }) =>
    active
      ? css`
          background-color: ${theme.palette.primary.main};
          color: white;
          font-weight: 600;
          transform: scale(1.02);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          z-index: 2;

          &::before {
            content: '';
            position: absolute;
            top: -2px;
            left: -2px;
            right: -2px;
            bottom: -2px;
            background: linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.main});
            border-radius: inherit;
            z-index: -1;
            opacity: 0.3;
          }
        `
      : css`
          background-color: ${theme.palette.background.default};
          color: ${theme.palette.text.primary};
          font-weight: 500;
          transform: scale(1);
          box-shadow: none;
          z-index: 1;
        `}
`

// Loading spinner animation
export const spinnerRotation = keyframes`
  to {
    transform: rotate(360deg);
  }
`

export const LoadingSpinner = styled.span`
  display: inline-block;
  width: 16px;
  height: 16px;
  margin-right: 8px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: ${spinnerRotation} 0.75s linear infinite;
`

export const SavingsBadge = styled.div`
  position: absolute;
  top: 1.2rem;
  right: -3.2rem;
  background-color: ${({ theme }) => theme.palette.success.main};
  color: white;
  padding: 0.35rem 0;
  font-size: 0.75rem;
  font-weight: 600;
  z-index: 1;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transform: rotate(45deg);
  text-align: center;
  width: 200px;
  letter-spacing: 0.5px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      45deg,
      rgba(255, 255, 255, 0.1) 0%,
      rgba(255, 255, 255, 0.2) 50%,
      rgba(255, 255, 255, 0.1) 100%
    );
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border: 1px solid rgba(255, 255, 255, 0.2);
    pointer-events: none;
  }

  animation: ${keyframes`
    0% {
      transform: rotate(45deg) scale(1);
    }
    50% {
      transform: rotate(45deg) scale(1.03);
    }
    100% {
      transform: rotate(45deg) scale(1);
    }
  `} 2s ease-in-out infinite;
`

export const TrialBadge = styled.div`
  background-color: ${({ theme }) => theme.palette.primary.light};
  color: #fff;
  padding: 0.5rem 1rem;
  border-radius: 1rem;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  display: inline-block;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
`
